import React from 'react';
import {
  LAYOUT_FIELD_DEFS,
  LAYOUT_LINE_MIN_THICKNESS_PT,
  lineThicknessPt,
  PDF_A4_HEIGHT_PT,
  PDF_A4_WIDTH_PT,
  layoutTablePlacement,
  resolveDocumentPageMargins,
  resolveLineOrientation,
  TABLE_START_MIN_POSITIONS,
  type DocumentLayoutV1,
  type LayoutBand,
  type LayoutElement,
  type LayoutElementKey,
} from '../../utils/documentLayoutTypes';
import {
  DOCUMENT_FOLD_MARK_COLOR,
  layoutShowsFoldMarks,
  resolveDocumentFoldMarkRects,
} from '../../utils/documentFoldMarks';
import {
  LAYOUT_SAMPLE_DATA,
  applyAspectLockedCornerResize,
  clampBandHeight,
  intrinsicContentHeightPt,
  isLayoutImageKey,
  isLayoutResizeCorner,
  sampleContentForElement,
  sampleTokenValues,
  sampleValueForLabeledField,
  type LayoutSampleData,
} from './layoutEditorUtils';
import { isLabeledFieldKey } from '../../utils/documentLayoutTypes';
import {
  documentNumberFieldLabel,
  documentTitleShowsType,
  inlineLabelValueSeparator,
  issueDateFieldLabel,
  labeledFieldPresentation,
  leistungszeitraumFieldLabel,
  parseFreeTextWithTokens,
} from '../../utils/layoutFieldPresentation';
import { OFFER_PDF_FONT_STYLE } from '../../../leads/components/HtmlLayoutBands';
/** Gap between the editable page and the read-only continuation page. */
const PREVIEW_PAGE_GAP_PT = 24;

const SNAP_THRESHOLD = 6;

function layoutElementLabel(key: LayoutElementKey, band: LayoutBand): string {
  return LAYOUT_FIELD_DEFS.find((d) => d.key === key && d.band === band)?.label ?? key;
}

function resolveDropBand(
  key: string,
  pageY: number,
  footerTop: number,
  preferred?: LayoutBand,
): LayoutBand {
  const allowed = LAYOUT_FIELD_DEFS.filter((d) => d.key === key).map((d) => d.band);
  if (allowed.length === 0) return preferred ?? 'header';
  if (allowed.length === 1) return allowed[0];
  const byY: LayoutBand = pageY >= footerTop ? 'footer' : 'header';
  if (allowed.includes(byY)) return byY;
  if (preferred && allowed.includes(preferred)) return preferred;
  return allowed[0];
}

type Props = {
  layout: DocumentLayoutV1;
  activeBand: LayoutBand;
  selectedId: string | null;
  multiSelectIds: string[];
  zoom: number;
  showGuides: boolean;
  previewMode: boolean;
  /** Real company logo URL for the logo element (null = placeholder). */
  logoUrl?: string | null;
  /** Resolved preview URLs for banner/signature elements keyed by element id. */
  assetUrls?: Record<string, string>;
  /** Live sample content (current user + company); falls back to LAYOUT_SAMPLE_DATA. */
  sampleData?: LayoutSampleData;
  /** Anfangstext from Textvorlagen (Rechnung). Empty hides the intro in the body preview. */
  introParas?: string[];
  /** Ctrl/Cmd + scroll zooms the canvas (delta in zoom units, e.g. ±0.05). */
  onZoomNudge?: (delta: number) => void;
  onCommitBandHeight: (band: LayoutBand, heightPt: number, opts?: { recordHistory?: boolean }) => void;
  onSelect: (id: string | null, opts?: { shift?: boolean }) => void;
  /** Rubber-band selection of element ids (page coordinates intersection). */
  onMarqueeSelect?: (ids: string[]) => void;
  onBandChange: (band: LayoutBand) => void;
  onCommitElements: (band: LayoutBand, elements: LayoutElement[], opts?: { recordHistory?: boolean }) => void;
  onDropPalette: (
    payload: { key: string; band: LayoutBand; lineOrientation?: 'horizontal' | 'vertical' },
    xPt: number,
    yPt: number,
  ) => void;
  onContextMenu: (id: string, x: number, y: number) => void;
};

type BandEl = LayoutElement & { band: LayoutBand };

const BODY_SAMPLE_ROWS = [
  {
    pos: '1',
    qty: '1,00',
    unit: 'psch',
    title: 'Heizlastberechnung',
    desc: 'Berechnung nach DIN EN 12831 für Bürogebäude, inkl. Dokumentation',
    price: '1.280,00 €',
    total: '1.280,00 €',
  },
  {
    pos: '2',
    qty: '4,00',
    unit: 'Stk',
    title: 'Thermostatventil DN 15',
    desc: 'Inkl. Montage und Einregulierung',
    price: '86,50 €',
    total: '346,00 €',
  },
  {
    pos: '3',
    qty: '12,00',
    unit: 'm',
    title: 'Kupferleitung 15×1 mm',
    desc: 'Verlegt und gedämmt',
    price: '18,90 €',
    total: '226,80 €',
  },
] as const;

/**
 * Non-editable body preview matching OfferDocumentEditor / OfferPdfDocument look.
 * part: 'intro' = page-1 text before a table page break, 'rest' = everything after it.
 * Marks the table start and the TABLE_START_MIN_POSITIONS-th row for the „Automatisch“ measurement.
 */
function BodyFlowPreview({
  tableHeaderBg,
  fontSizePt,
  tableStartsOnPage2,
  introParas,
  part = 'all',
}: {
  tableHeaderBg: string;
  fontSizePt: number;
  tableStartsOnPage2?: boolean;
  introParas?: string[];
  part?: 'all' | 'intro' | 'rest';
}) {
  const fs = fontSizePt;
  const text = '#262626'; // neutral-800
  const muted = '#525252'; // neutral-600
  const posMuted = '#525252';

  return (
    <div
      // No own clipping: the page body boxes clip, and page 1 measures overflow for the page-2 preview.
      style={{
        width: '100%',
        ...OFFER_PDF_FONT_STYLE,
        fontSize: fs,
        lineHeight: 1.45,
        color: text,
      }}
    >
      {part !== 'rest' && introParas && introParas.length > 0 ? (
        <div style={{ marginBottom: 8 }}>
          {introParas.map((para, i) => (
            <div key={`intro-${i}`} style={{ marginBottom: 8 }}>
              {para}
            </div>
          ))}
        </div>
      ) : null}

      {part === 'intro' ? null : (
      <>
      {part === 'all' && tableStartsOnPage2 ? (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            margin: '10px 0 12px',
            color: '#64748b',
            fontSize: Math.max(7, fs - 1),
            fontWeight: 600,
            letterSpacing: 0.2,
          }}
        >
          <span style={{ flex: 1, borderTop: '1px dashed #94a3b8' }} />
          Seite 2
          <span style={{ flex: 1, borderTop: '1px dashed #94a3b8' }} />
        </div>
      ) : null}

      <table
        data-table-start=""
        style={{
          width: '100%',
          borderCollapse: 'collapse',
          tableLayout: 'fixed',
          marginTop: 6,
          marginBottom: 22,
        }}
      >
        <colgroup>
          <col style={{ width: '7%' }} />
          <col style={{ width: '11%' }} />
          <col style={{ width: '10%' }} />
          <col />
          <col style={{ width: '14%' }} />
          <col style={{ width: '14%' }} />
        </colgroup>
        <thead>
          <tr
            style={{
              background: tableHeaderBg,
              fontSize: fs,
              fontWeight: 400,
              color: '#171717',
            }}
          >
            <th
              style={{
                padding: '6px 8px 6px 0',
                textAlign: 'left',
                fontWeight: 400,
                borderBottom: '1px solid rgba(203,213,225,0.9)',
              }}
            >
              Pos.
            </th>
            <th
              style={{
                padding: '6px 8px',
                textAlign: 'left',
                fontWeight: 400,
                borderBottom: '1px solid rgba(203,213,225,0.9)',
              }}
            >
              Menge
            </th>
            <th
              style={{
                padding: '6px 8px',
                textAlign: 'left',
                fontWeight: 400,
                borderBottom: '1px solid rgba(203,213,225,0.9)',
              }}
            >
              Einheit
            </th>
            <th
              style={{
                padding: '6px 8px',
                textAlign: 'left',
                fontWeight: 400,
                borderBottom: '1px solid rgba(203,213,225,0.9)',
              }}
            >
              Beschreibung
            </th>
            <th
              style={{
                padding: '6px 0 6px 8px',
                textAlign: 'right',
                fontWeight: 400,
                borderBottom: '1px solid rgba(203,213,225,0.9)',
                whiteSpace: 'nowrap',
              }}
            >
              Preis
            </th>
            <th
              style={{
                padding: '6px 0 6px 8px',
                textAlign: 'right',
                fontWeight: 400,
                borderBottom: '1px solid rgba(203,213,225,0.9)',
                whiteSpace: 'nowrap',
              }}
            >
              Gesamt
            </th>
          </tr>
        </thead>
        <tbody>
          {BODY_SAMPLE_ROWS.map((row, index) => (
            <tr
              key={row.pos}
              data-table-anchor={
                index === Math.min(TABLE_START_MIN_POSITIONS, BODY_SAMPLE_ROWS.length) - 1 ? '' : undefined
              }
              style={{ borderBottom: '1px solid rgba(226,232,240,0.9)' }}
            >
              <td
                style={{
                  padding: '8px 8px 8px 0',
                  verticalAlign: 'top',
                  color: posMuted,
                  fontVariantNumeric: 'tabular-nums',
                }}
              >
                {row.pos}
              </td>
              <td
                style={{
                  padding: '8px',
                  verticalAlign: 'top',
                  textAlign: 'left',
                  fontVariantNumeric: 'tabular-nums',
                }}
              >
                {row.qty}
              </td>
              <td style={{ padding: '8px', verticalAlign: 'top' }}>{row.unit}</td>
              <td style={{ padding: '8px', verticalAlign: 'top' }}>
                <div>{row.title}</div>
                <div style={{ color: '#64748b' }}>{row.desc}</div>
              </td>
              <td
                style={{
                  padding: '8px 0 8px 8px',
                  verticalAlign: 'top',
                  textAlign: 'right',
                  whiteSpace: 'nowrap',
                  fontVariantNumeric: 'tabular-nums',
                }}
              >
                {row.price}
              </td>
              <td
                style={{
                  padding: '8px 0 8px 8px',
                  verticalAlign: 'top',
                  textAlign: 'right',
                  whiteSpace: 'nowrap',
                  fontVariantNumeric: 'tabular-nums',
                }}
              >
                {row.total}
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'flex-end',
          gap: 4,
          marginBottom: 24,
        }}
      >
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            gap: 40,
            minWidth: 220,
            fontWeight: 700,
            color: '#171717',
          }}
        >
          <span>Nettobetrag</span>
          <span style={{ fontVariantNumeric: 'tabular-nums' }}>1.852,80 €</span>
        </div>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            gap: 40,
            minWidth: 220,
            color: muted,
          }}
        >
          <span>zzgl. 19 % USt.</span>
          <span style={{ fontVariantNumeric: 'tabular-nums', color: '#171717', fontWeight: 400 }}>
            352,03 €
          </span>
        </div>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            gap: 40,
            minWidth: 220,
            marginTop: 4,
            paddingTop: 10,
            borderTop: '3px double #171717',
            fontWeight: 700,
            color: '#171717',
          }}
        >
          <span>Bruttobetrag</span>
          <span style={{ fontVariantNumeric: 'tabular-nums' }}>2.204,83 €</span>
        </div>
      </div>

      <div style={{ marginBottom: 24, color: muted }}>
        Zahlbar innerhalb von 14 Tagen ohne Abzug.
      </div>

      <div style={{ marginBottom: 48, color: '#171717' }}>Mit freundlichen Grüßen</div>

      <div style={{ maxWidth: 280, borderTop: '2px solid #171717', paddingTop: 10 }}>
        <div style={{ color: '#737373', fontSize: fs, lineHeight: 1.35 }}>
          Unterschrift / Stempel
        </div>
      </div>
      </>
      )}
    </div>
  );
}

function renderElementContent(
  el: LayoutElement,
  previewMode: boolean,
  logoUrl?: string | null,
  sampleData?: LayoutSampleData,
  assetUrl?: string | null,
  hideOfferTitleTypePrefix?: boolean,
) {
  if (el.key === 'logo') {
    if (logoUrl) {
      return (
        <img
          src={logoUrl}
          alt="Firmenlogo"
          draggable={false}
          style={{
            position: 'absolute',
            inset: 0,
            width: '100%',
            height: '100%',
            maxWidth: '100%',
            maxHeight: '100%',
            objectFit: 'contain',
            objectPosition: 'right center',
            pointerEvents: 'none',
            display: 'block',
          }}
        />
      );
    }
    return (
      <div
        style={{
          width: '100%',
          height: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          border: '1px dashed #d1d5db',
          borderRadius: 4,
          color: '#9ca3af',
          fontSize: 11,
          background: '#fafbfc',
        }}
      >
        Firmenlogo
      </div>
    );
  }
  if (el.key === 'headerBanner' || el.key === 'signatureImage') {
    const label = el.key === 'headerBanner' ? 'Bild' : 'Unterschrift';
    if (assetUrl) {
      return (
        <img
          src={assetUrl}
          alt={label}
          draggable={false}
          style={{
            position: 'absolute',
            inset: 0,
            width: '100%',
            height: '100%',
            objectFit: el.key === 'headerBanner' ? 'cover' : 'contain',
            objectPosition: 'center',
            pointerEvents: 'none',
            display: 'block',
            filter: el.key === 'headerBanner' && el.imageGrayscale ? 'grayscale(100%)' : undefined,
          }}
        />
      );
    }
    return (
      <div
        style={{
          width: '100%',
          height: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          border: '1px dashed #d1d5db',
          borderRadius: 4,
          color: '#9ca3af',
          fontSize: 11,
          background: '#fafbfc',
          textAlign: 'center',
          padding: 8,
        }}
      >
        {label}
      </div>
    );
  }
  if (el.key === 'divider' || el.key === 'colorBlock') return null;
  const sample = sampleData ?? LAYOUT_SAMPLE_DATA;
  const bodyColor = el.color || '#374151';
  const baseTextStyle: React.CSSProperties = {
    // Boxes are sized for the PDF's Helvetica metrics. The inherited app font is
    // SF Pro on macOS, 8-16% wider, which wrapped and clipped header/footer text.
    ...OFFER_PDF_FONT_STYLE,
    fontSize: el.fontSizePt ?? 9.5,
    color: bodyColor,
    fontWeight: el.bold ? 700 : 400,
    fontStyle: el.italic ? 'italic' : 'normal',
    textDecoration: el.underline ? 'underline' : 'none',
    // The PDF underline runs through every glyph; without this the browser skips
    // it around characters like "|" and canvas and export drift apart.
    textDecorationSkipInk: 'none',
    textAlign: el.align ?? 'left',
    lineHeight: 1.35,
    width: '100%',
    height: '100%',
    overflow: 'hidden',
  };

  if (isLabeledFieldKey(el.key)) {
    const defaultLabel =
      el.key === 'documentTitle'
        ? sample.documentType
        : el.key === 'invoiceNumber'
          ? documentNumberFieldLabel(sample.documentType)
          : el.key === 'issueDate'
            ? issueDateFieldLabel(sample.documentType)
            : el.key === 'leistungszeitraum'
              ? leistungszeitraumFieldLabel(sample.documentType) || undefined
              : undefined;
    const pres = labeledFieldPresentation(
      el,
      sampleValueForLabeledField(el.key, sample),
      defaultLabel ? { defaultLabel } : undefined,
    );
    if (pres.placement === 'inline') {
      const sep = inlineLabelValueSeparator(el.key);
      return (
        <div style={{ ...baseTextStyle, fontWeight: 400, color: pres.valueColor, fontSize: pres.valueFontSizePt }}>
          {pres.label ? (
            <span
              style={{
                fontWeight: pres.labelBold ? 700 : 400,
                color: pres.labelColor,
                fontSize: pres.labelFontSizePt,
              }}
            >
              {pres.label}
              {sep}
            </span>
          ) : null}
          <span style={{ fontWeight: pres.valueBold ? 700 : 400, color: pres.valueColor }}>
            {pres.value}
          </span>
        </div>
      );
    }
    return (
      <div style={{ ...baseTextStyle, fontWeight: 400, color: pres.valueColor, fontSize: pres.valueFontSizePt }}>
        {pres.label ? (
          <div
            style={{
              color: pres.labelColor,
              fontWeight: pres.labelBold ? 700 : 400,
              fontSize: pres.labelFontSizePt,
              marginBottom: 2,
              width: '100%',
              textAlign: el.align ?? 'left',
            }}
          >
            {pres.label}
          </div>
        ) : null}
        <div
          style={{
            fontWeight: pres.valueBold ? 700 : 400,
            color: pres.valueColor,
            width: '100%',
            textAlign: el.align ?? 'left',
          }}
        >
          {pres.value}
        </div>
      </div>
    );
  }

  if (el.key === 'freeText') {
    const raw = el.text?.trim() || 'Freitext';
    const segments = parseFreeTextWithTokens(raw, sampleTokenValues(sample));
    return (
      <div style={{ ...baseTextStyle, fontWeight: el.bold ? 700 : 400, whiteSpace: 'pre-wrap' }}>
        {segments.map((seg, i) => (
          <span key={i} style={{ fontWeight: seg.boldValue || el.bold ? 700 : 400 }}>
            {seg.text}
          </span>
        ))}
      </div>
    );
  }

  const content = sampleContentForElement(el, previewMode, sample, {
    hideOfferTitleTypePrefix,
  });
  const isMultilineBlock = el.key === 'recipient' || el.key === 'ansprechpartner';
  return (
    <div
      style={{
        ...baseTextStyle,
        // Match OfferPdfDocument / PdfLayoutBands line rhythm (marginBottom: 2).
        lineHeight: isMultilineBlock ? 1.25 : baseTextStyle.lineHeight,
      }}
    >
      {content.title ? (
        <div
          style={
            content.titleBold
              ? { color: bodyColor, fontWeight: 700, marginBottom: 2 }
              : { color: '#64748b', fontSize: Math.max(5, (el.fontSizePt ?? 9.5) - 1), marginBottom: 2 }
          }
        >
          {content.title}
        </div>
      ) : null}
      {content.lines.map((ln, i) => (
        <div
          key={i}
          style={{
            ...(content.linesBold ? { fontWeight: 700 } : undefined),
            ...(isMultilineBlock && i < content.lines.length - 1 ? { marginBottom: 2 } : undefined),
          }}
        >
          {ln}
        </div>
      ))}
    </div>
  );
}

export function LayoutCanvas({
  layout,
  activeBand,
  selectedId,
  multiSelectIds,
  zoom,
  showGuides,
  previewMode,
  logoUrl = null,
  assetUrls = {},
  sampleData,
  introParas,
  onZoomNudge,
  onCommitBandHeight,
  onSelect,
  onMarqueeSelect,
  onBandChange,
  onCommitElements,
  onDropPalette,
  onContextMenu,
}: Props) {
  const canvasRef = React.useRef<HTMLDivElement | null>(null);
  const viewportRef = React.useRef<HTMLDivElement | null>(null);
  const freeTextEditRef = React.useRef<HTMLTextAreaElement | null>(null);
  const zoomRef = React.useRef(zoom);
  zoomRef.current = zoom;
  const appliedZoomRef = React.useRef(zoom);
  /** Page point the next zoom step should keep in place (cursor, else viewport centre). */
  const zoomAnchorRef = React.useRef<{
    pageX: number;
    pageY: number;
    clientX: number;
    clientY: number;
  } | null>(null);
  const viewportCentrePageRef = React.useRef<{ x: number; y: number } | null>(null);
  const [guideV, setGuideV] = React.useState<number | null>(null);
  const [guideH, setGuideH] = React.useState<number | null>(null);
  const [editingFreeTextId, setEditingFreeTextId] = React.useState<string | null>(null);
  const [freeTextDraft, setFreeTextDraft] = React.useState('');
  const editingFreeTextIdRef = React.useRef<string | null>(null);
  const freeTextDraftRef = React.useRef('');
  editingFreeTextIdRef.current = editingFreeTextId;
  freeTextDraftRef.current = freeTextDraft;
  const [marquee, setMarquee] = React.useState<{
    x0: number;
    y0: number;
    x1: number;
    y1: number;
  } | null>(null);
  const marqueeMovedRef = React.useRef(false);
  const skipClickDeselectRef = React.useRef(false);

  /** Remembers what sits in the middle, so toolbar zoom keeps that spot instead of the page corner. */
  const recordViewportCentre = React.useCallback(() => {
    const viewport = viewportRef.current;
    const canvas = canvasRef.current;
    if (!viewport || !canvas) return;
    const view = viewport.getBoundingClientRect();
    const page = canvas.getBoundingClientRect();
    viewportCentrePageRef.current = {
      x: (view.left + view.width / 2 - page.left) / zoomRef.current,
      y: (view.top + view.height / 2 - page.top) / zoomRef.current,
    };
  }, []);

  React.useEffect(() => {
    const el = viewportRef.current;
    if (!el || !onZoomNudge) return;
    const onWheel = (e: WheelEvent) => {
      if (!(e.ctrlKey || e.metaKey)) return;
      e.preventDefault();
      const page = canvasRef.current?.getBoundingClientRect();
      if (page) {
        zoomAnchorRef.current = {
          pageX: (e.clientX - page.left) / zoomRef.current,
          pageY: (e.clientY - page.top) / zoomRef.current,
          clientX: e.clientX,
          clientY: e.clientY,
        };
      }
      // Trackpads send many small deltas; normalize to stepped nudges.
      const magnitude = Math.min(3, Math.max(1, Math.round(Math.abs(e.deltaY) / 40)));
      const direction = e.deltaY > 0 ? -1 : 1;
      onZoomNudge(direction * 0.05 * magnitude);
    };
    el.addEventListener('wheel', onWheel, { passive: false });
    return () => el.removeEventListener('wheel', onWheel);
  }, [onZoomNudge]);

  React.useEffect(() => {
    recordViewportCentre();
  }, [recordViewportCentre]);

  /** Scale grows the page from its top-left corner — scroll back so the anchor stays put. */
  React.useLayoutEffect(() => {
    const viewport = viewportRef.current;
    const canvas = canvasRef.current;
    if (!viewport || !canvas) return;
    if (appliedZoomRef.current === zoom) return;
    appliedZoomRef.current = zoom;
    const anchor = zoomAnchorRef.current;
    zoomAnchorRef.current = null;
    const centre = viewportCentrePageRef.current;
    const page = canvas.getBoundingClientRect();
    if (anchor) {
      viewport.scrollLeft += page.left + anchor.pageX * zoom - anchor.clientX;
      viewport.scrollTop += page.top + anchor.pageY * zoom - anchor.clientY;
    } else if (centre) {
      const view = viewport.getBoundingClientRect();
      viewport.scrollLeft += page.left + centre.x * zoom - (view.left + view.width / 2);
      viewport.scrollTop += page.top + centre.y * zoom - (view.top + view.height / 2);
    }
    recordViewportCentre();
  }, [zoom, recordViewportCentre]);

  const footerTop = PDF_A4_HEIGHT_PT - layout.footer.heightPt;
  const pageMargins = resolveDocumentPageMargins(layout);
  const bodyTop = Math.max(layout.header.heightPt + 8, pageMargins.top);
  const bodyBottom = Math.max(layout.footer.heightPt + 12, pageMargins.bottom);

  // Wie im PDF: läuft der Rumpf über Seite 1 hinaus, zeigt eine zweite Seite die Fortsetzung
  // (Kopfbereich nur auf Seite 1, Fußbereich auf jeder Seite).
  const page1BodyRef = React.useRef<HTMLDivElement>(null);
  const tablePlacement = layoutTablePlacement(layout);

  // „Automatisch“ wie im PDF: eine unsichtbare, fortlaufende Kopie misst, ob Tabellenkopf und die
  // ersten Positionen noch auf Seite 1 passen; sonst beginnt die Tabelle auf Seite 2.
  const autoMeasureRef = React.useRef<HTMLDivElement>(null);
  const [autoTableBreak, setAutoTableBreak] = React.useState(false);
  React.useLayoutEffect(() => {
    const page = page1BodyRef.current;
    const copy = autoMeasureRef.current;
    if (tablePlacement !== 'auto' || !page || !copy) {
      setAutoTableBreak(false);
      return;
    }
    const check = () => {
      const table = copy.querySelector('[data-table-start]');
      const anchor = copy.querySelector('[data-table-anchor]');
      if (!table || !anchor || !copy.offsetHeight) return;
      // Seite ist per CSS skaliert: Bildschirm-Abstände auf pt zurückrechnen.
      const box = copy.getBoundingClientRect();
      const scale = box.height / copy.offsetHeight;
      const tableTop = (table.getBoundingClientRect().top - box.top) / scale;
      const anchorBottom = (anchor.getBoundingClientRect().bottom - box.top) / scale;
      const limit = page.clientHeight;
      setAutoTableBreak(tableTop < limit && anchorBottom > limit);
    };
    check();
    const observer = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(check) : null;
    observer?.observe(copy);
    return () => observer?.disconnect();
  });
  const tableStartsOnPage2 = tablePlacement === 'page2' || (tablePlacement === 'auto' && autoTableBreak);
  const [page1Overflows, setPage1Overflows] = React.useState(false);
  React.useLayoutEffect(() => {
    const node = page1BodyRef.current;
    if (!node || tableStartsOnPage2) {
      setPage1Overflows(false);
      return;
    }
    const check = () => setPage1Overflows(node.scrollHeight > node.clientHeight + 1);
    check();
    const observer = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(check) : null;
    observer?.observe(node);
    return () => observer?.disconnect();
  });
  const showPage2 = tableStartsOnPage2 || page1Overflows;
  const page1BodyHeight = PDF_A4_HEIGHT_PT - bodyTop - bodyBottom;

  /** Betreff zeigt den Typ nur, wenn die Belegzeile ihn nicht schon nennt — wie im Dokument. */
  const hideOfferTitleTypePrefix = documentTitleShowsType(
    layout,
    (sampleData ?? LAYOUT_SAMPLE_DATA).documentType,
  );

  const allElements: BandEl[] = React.useMemo(
    () => [
      ...layout.header.elements.map((e) => ({ ...e, band: 'header' as const })),
      ...layout.footer.elements.map((e) => ({ ...e, band: 'footer' as const })),
    ],
    [layout],
  );

  const finishFreeTextEdit = React.useCallback(
    (opts?: { cancel?: boolean }) => {
      const id = editingFreeTextIdRef.current;
      if (!id) return;
      const el = allElements.find((e) => e.id === id);
      const draft = freeTextDraftRef.current;
      editingFreeTextIdRef.current = null;
      setEditingFreeTextId(null);
      if (!el || el.key !== 'freeText' || opts?.cancel) return;
      if ((el.text ?? '') === draft) return;
      const band = el.band;
      const list = band === 'header' ? layout.header.elements : layout.footer.elements;
      const next = list.map((x) => (x.id === id ? { ...x, text: draft } : x));
      onCommitElements(band, next, { recordHistory: true });
    },
    [allElements, layout.footer.elements, layout.header.elements, onCommitElements],
  );

  const beginFreeTextEdit = (el: BandEl) => {
    if (previewMode || el.locked || el.key !== 'freeText') return;
    if (editingFreeTextId && editingFreeTextId !== el.id) {
      finishFreeTextEdit();
    }
    setEditingFreeTextId(el.id);
    setFreeTextDraft(el.text ?? '');
    onBandChange(el.band);
    onSelect(el.id);
    requestAnimationFrame(() => {
      const ta = freeTextEditRef.current;
      if (!ta) return;
      ta.focus();
      ta.select();
    });
  };

  React.useEffect(() => {
    if (previewMode && editingFreeTextId) finishFreeTextEdit();
  }, [previewMode, editingFreeTextId, finishFreeTextEdit]);

  const pagePointFromClient = (clientX: number, clientY: number) => {
    const rect = canvasRef.current?.getBoundingClientRect();
    if (!rect) return null;
    return {
      x: (clientX - rect.left) / zoom,
      y: (clientY - rect.top) / zoom,
    };
  };

  const elementsIntersectingRect = (x0: number, y0: number, x1: number, y1: number) => {
    const left = Math.min(x0, x1);
    const top = Math.min(y0, y1);
    const right = Math.max(x0, x1);
    const bottom = Math.max(y0, y1);
    return allElements
      .filter((el) => {
        const bandTop = el.band === 'footer' ? footerTop : 0;
        const elL = el.xPt;
        const elT = bandTop + el.yPt;
        const elR = elL + el.wPt;
        const elB = elT + el.hPt;
        return !(elR < left || elL > right || elB < top || elT > bottom);
      })
      .map((el) => el.id);
  };

  const startMarquee = (e: React.MouseEvent) => {
    if (previewMode || e.button !== 0) return;
    const target = e.target as HTMLElement;
    if (target.closest('[data-id]')) return;
    if (target.closest('[data-resize]')) return;
    if (target.closest('[data-band-resize]')) return;
    if (target.closest('[data-freetext-edit]')) return;
    const pt = pagePointFromClient(e.clientX, e.clientY);
    if (!pt) return;
    e.preventDefault();
    e.stopPropagation();
    if (editingFreeTextId) finishFreeTextEdit();
    marqueeMovedRef.current = false;
    setMarquee({ x0: pt.x, y0: pt.y, x1: pt.x, y1: pt.y });

    const onMove = (ev: MouseEvent) => {
      const next = pagePointFromClient(ev.clientX, ev.clientY);
      if (!next) return;
      marqueeMovedRef.current = true;
      setMarquee((prev) => (prev ? { ...prev, x1: next.x, y1: next.y } : prev));
    };

    const onUp = (ev: MouseEvent) => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
      const end = pagePointFromClient(ev.clientX, ev.clientY);
      setMarquee((prev) => {
        if (!prev) return null;
        const box = end ? { ...prev, x1: end.x, y1: end.y } : prev;
        const w = Math.abs(box.x1 - box.x0);
        const h = Math.abs(box.y1 - box.y0);
        skipClickDeselectRef.current = true;
        const ids =
          w < 3 && h < 3 ? null : elementsIntersectingRect(box.x0, box.y0, box.x1, box.y1);
        // Schedule outside of setState to avoid parent updates during render phase.
        queueMicrotask(() => {
          if (ids == null) onSelect(null);
          else if (onMarqueeSelect) onMarqueeSelect(ids);
        });
        return null;
      });
    };

    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  };

  const onDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'copy';
  };

  const onDrop = (e: React.DragEvent) => {
    e.preventDefault();
    const raw = e.dataTransfer.getData('application/x-layout-palette');
    if (!raw || !canvasRef.current) return;
    try {
      const payload = JSON.parse(raw) as {
        key: string;
        band: LayoutBand;
        lineOrientation?: 'horizontal' | 'vertical';
      };
      const rect = canvasRef.current.getBoundingClientRect();
      const pageX = (e.clientX - rect.left) / zoom;
      const pageY = (e.clientY - rect.top) / zoom;
      const band = resolveDropBand(payload.key, pageY, footerTop, payload.band);
      const bandTop = band === 'footer' ? footerTop : 0;
      const bandH = band === 'footer' ? layout.footer.heightPt : layout.header.heightPt;
      const xPt = Math.max(0, Math.min(PDF_A4_WIDTH_PT - 40, pageX - 20));
      const yPt = Math.max(0, Math.min(bandH - 20, pageY - bandTop - 10));
      onBandChange(band);
      onDropPalette(
        { key: payload.key, band, lineOrientation: payload.lineOrientation },
        xPt,
        yPt,
      );
    } catch {
      /* ignore */
    }
  };

  const startDrag = (el: BandEl, e: React.MouseEvent, groupIds?: string[]) => {
    if (previewMode || el.locked || e.button !== 0) return;
    if (el.band !== activeBand) {
      onBandChange(el.band);
    }
    e.preventDefault();
    e.stopPropagation();
    const band = el.band;
    const bandHeight = band === 'header' ? layout.header.heightPt : layout.footer.heightPt;
    const bandEls =
      band === 'header' ? layout.header.elements.map((x) => ({ ...x })) : layout.footer.elements.map((x) => ({ ...x }));
    const moveIds = (groupIds?.length ? groupIds : [el.id]).filter((id) => {
      const item = bandEls.find((x) => x.id === id);
      return item && !item.locked;
    });
    const origins = new Map(moveIds.map((id) => {
      const item = bandEls.find((x) => x.id === id)!;
      return [id, { x: item.xPt, y: item.yPt }] as const;
    }));
    const startX = e.clientX;
    const startY = e.clientY;
    let moved = false;
    let live = bandEls;

    const onMove = (ev: MouseEvent) => {
      moved = true;
      const rawDx = (ev.clientX - startX) / zoom;
      const rawDy = (ev.clientY - startY) / zoom;

      // Clamp group delta so no member leaves the band.
      let dx = rawDx;
      let dy = rawDy;
      for (const id of moveIds) {
        const orig = origins.get(id)!;
        const item = live.find((x) => x.id === id)!;
        const minDx = -orig.x;
        const maxDx = PDF_A4_WIDTH_PT - item.wPt - orig.x;
        const minDy = -orig.y;
        const maxDy = bandHeight - item.hPt - orig.y;
        dx = Math.max(minDx, Math.min(maxDx, dx));
        dy = Math.max(minDy, Math.min(maxDy, dy));
      }

      let nx = (origins.get(el.id)?.x ?? el.xPt) + dx;
      let ny = (origins.get(el.id)?.y ?? el.yPt) + dy;

      let gv: number | null = null;
      let gh: number | null = null;
      // PowerPoint-style: Alt temporarily disables snap / smart guides.
      if (showGuides && !ev.altKey && moveIds.length === 1) {
        const xTargets = [0, PDF_A4_WIDTH_PT / 2, PDF_A4_WIDTH_PT];
        const yTargets = [0, bandHeight / 2, bandHeight];
        for (const o of live) {
          if (o.id === el.id) continue;
          xTargets.push(o.xPt, o.xPt + o.wPt / 2, o.xPt + o.wPt);
          yTargets.push(o.yPt, o.yPt + o.hPt / 2, o.yPt + o.hPt);
        }
        const xCand = [
          { pos: nx, off: 0 },
          { pos: nx + el.wPt / 2, off: el.wPt / 2 },
          { pos: nx + el.wPt, off: el.wPt },
        ];
        const yCand = [
          { pos: ny, off: 0 },
          { pos: ny + el.hPt / 2, off: el.hPt / 2 },
          { pos: ny + el.hPt, off: el.hPt },
        ];
        let bestX: { d: number; target: number; off: number } | null = null;
        let bestY: { d: number; target: number; off: number } | null = null;
        for (const c of xCand) {
          for (const t of xTargets) {
            const d = Math.abs(c.pos - t);
            if (d <= SNAP_THRESHOLD && (!bestX || d < bestX.d)) bestX = { d, target: t, off: c.off };
          }
        }
        for (const c of yCand) {
          for (const t of yTargets) {
            const d = Math.abs(c.pos - t);
            if (d <= SNAP_THRESHOLD && (!bestY || d < bestY.d)) bestY = { d, target: t, off: c.off };
          }
        }
        if (bestX) {
          nx = bestX.target - bestX.off;
          gv = bestX.target;
          dx = nx - (origins.get(el.id)?.x ?? el.xPt);
        }
        if (bestY) {
          ny = bestY.target - bestY.off;
          gh = bestY.target;
          dy = ny - (origins.get(el.id)?.y ?? el.yPt);
        }
        // Re-clamp after snap.
        for (const id of moveIds) {
          const orig = origins.get(id)!;
          const item = live.find((x) => x.id === id)!;
          const minDx = -orig.x;
          const maxDx = PDF_A4_WIDTH_PT - item.wPt - orig.x;
          const minDy = -orig.y;
          const maxDy = bandHeight - item.hPt - orig.y;
          dx = Math.max(minDx, Math.min(maxDx, dx));
          dy = Math.max(minDy, Math.min(maxDy, dy));
        }
      }

      live = live.map((x) => {
        if (!moveIds.includes(x.id)) return x;
        const orig = origins.get(x.id)!;
        return { ...x, xPt: orig.x + dx, yPt: orig.y + dy };
      });
      setGuideV(gv);
      setGuideH(gh);
      onCommitElements(band, live, { recordHistory: false });
    };

    const onUp = () => {
      setGuideV(null);
      setGuideH(null);
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
      if (!moved) return;
      onCommitElements(band, live, { recordHistory: true });
    };

    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  };

  const startResize = (
    el: BandEl,
    e: React.MouseEvent,
    edge: 'n' | 's' | 'e' | 'w' | 'ne' | 'nw' | 'se' | 'sw',
  ) => {
    if (previewMode || el.locked) return;
    e.preventDefault();
    e.stopPropagation();
    const band = el.band;
    const bandHeight = band === 'header' ? layout.header.heightPt : layout.footer.heightPt;
    const bandEls =
      band === 'header' ? layout.header.elements.map((x) => ({ ...x })) : layout.footer.elements.map((x) => ({ ...x }));
    const startX = e.clientX;
    const startY = e.clientY;
    const orig = { x: el.xPt, y: el.yPt, w: el.wPt, h: el.hPt };
    let live = bandEls;
    const verticalDivider = el.key === 'divider' && el.lineOrientation === 'vertical';
    const horizontalDivider = el.key === 'divider' && el.lineOrientation !== 'vertical';
    const minW =
      verticalDivider
        ? LAYOUT_LINE_MIN_THICKNESS_PT
        : el.key === 'headerBanner'
          ? 1
          : el.key === 'logo' || el.key === 'signatureImage'
            ? 24
            : horizontalDivider
              ? 24
              : 40;
    const minH = verticalDivider
      ? 24
      : horizontalDivider
        ? LAYOUT_LINE_MIN_THICKNESS_PT
        : el.key === 'headerBanner'
          ? 1
          : el.key === 'logo' || el.key === 'signatureImage'
            ? 24
            : Math.max(14, intrinsicContentHeightPt(el));

    const onMove = (ev: MouseEvent) => {
      const dx = (ev.clientX - startX) / zoom;
      const dy = (ev.clientY - startY) / zoom;
      let x = orig.x;
      let y = orig.y;
      let w = orig.w;
      let h = orig.h;

      const lockAspect =
        ev.shiftKey && isLayoutImageKey(el.key) && isLayoutResizeCorner(edge);

      if (lockAspect) {
        const next = applyAspectLockedCornerResize(orig, edge, dx, dy, {
          minW,
          minH,
          maxX: PDF_A4_WIDTH_PT,
          maxY: bandHeight,
        });
        x = next.x;
        y = next.y;
        w = next.w;
        h = next.h;
      } else {
        if (edge.includes('e')) {
          w = Math.max(minW, Math.min(PDF_A4_WIDTH_PT - orig.x, orig.w + dx));
        }
        if (edge.includes('s')) {
          h = Math.max(minH, Math.min(bandHeight - orig.y, orig.h + dy));
        }
        if (edge.includes('w')) {
          const nextW = Math.max(minW, orig.w - dx);
          const nextX = orig.x + (orig.w - nextW);
          if (nextX >= 0) {
            x = nextX;
            w = nextW;
          } else {
            w = orig.x + orig.w;
            x = 0;
          }
        }
        if (edge.includes('n')) {
          const nextH = Math.max(minH, orig.h - dy);
          const nextY = orig.y + (orig.h - nextH);
          if (nextY >= 0) {
            y = nextY;
            h = nextH;
          } else {
            h = orig.y + orig.h;
            y = 0;
          }
        }

        x = Math.max(0, Math.min(PDF_A4_WIDTH_PT - w, x));
        y = Math.max(0, Math.min(bandHeight - h, y));
      }

      live = live.map((item) => (item.id === el.id ? { ...item, xPt: x, yPt: y, wPt: w, hPt: h } : item));
      onCommitElements(band, live, { recordHistory: false });
    };
    const onUp = () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
      onCommitElements(band, live, { recordHistory: true });
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  };

  const resizeHandles: Array<{
    edge: 'n' | 's' | 'e' | 'w' | 'ne' | 'nw' | 'se' | 'sw';
    cursor: string;
    style: React.CSSProperties;
    knob?: React.CSSProperties;
  }> = [
    {
      edge: 'n',
      cursor: 'ns-resize',
      style: { left: 8, right: 8, top: -4, height: 8, display: 'flex', justifyContent: 'center' },
      knob: { width: 18, height: 4, borderRadius: 2, background: '#3b82f6', marginTop: 2 },
    },
    {
      edge: 's',
      cursor: 'ns-resize',
      style: { left: 8, right: 8, bottom: -4, height: 8, display: 'flex', justifyContent: 'center', alignItems: 'flex-end' },
      knob: { width: 18, height: 4, borderRadius: 2, background: '#3b82f6', marginBottom: 2 },
    },
    {
      edge: 'e',
      cursor: 'ew-resize',
      style: { top: 8, bottom: 8, right: -4, width: 8, display: 'flex', alignItems: 'center' },
      knob: { width: 4, height: 18, borderRadius: 2, background: '#3b82f6', marginLeft: 2 },
    },
    {
      edge: 'w',
      cursor: 'ew-resize',
      style: { top: 8, bottom: 8, left: -4, width: 8, display: 'flex', alignItems: 'center', justifyContent: 'flex-start' },
      knob: { width: 4, height: 18, borderRadius: 2, background: '#3b82f6', marginRight: 2 },
    },
    { edge: 'nw', cursor: 'nwse-resize', style: { left: -5, top: -5, width: 10, height: 10 } },
    { edge: 'ne', cursor: 'nesw-resize', style: { right: -5, top: -5, width: 10, height: 10 } },
    { edge: 'sw', cursor: 'nesw-resize', style: { left: -5, bottom: -5, width: 10, height: 10 } },
    { edge: 'se', cursor: 'nwse-resize', style: { right: -5, bottom: -5, width: 10, height: 10 } },
  ];

  const startBandResize = (band: LayoutBand, e: React.MouseEvent) => {
    if (previewMode) return;
    e.preventDefault();
    e.stopPropagation();
    onBandChange(band);
    const startY = e.clientY;
    const origH = band === 'header' ? layout.header.heightPt : layout.footer.heightPt;
    let live = origH;

    const onMove = (ev: MouseEvent) => {
      const dy = (ev.clientY - startY) / zoom;
      // Header: drag bottom edge down → taller. Footer: drag top edge up → taller.
      const next = band === 'header' ? origH + dy : origH - dy;
      live = clampBandHeight(layout, band, next);
      onCommitBandHeight(band, live, { recordHistory: false });
    };
    const onUp = () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
      onCommitBandHeight(band, live, { recordHistory: true });
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  };

  const renderBandFrame = (band: LayoutBand) => {
    const top = band === 'header' ? 0 : footerTop;
    const height = band === 'header' ? layout.header.heightPt : layout.footer.heightPt;
    const edgeColor = '#cbd5e1';
    return (
      <div
        key={`frame-${band}`}
        style={{
          position: 'absolute',
          left: 0,
          top,
          width: PDF_A4_WIDTH_PT,
          height,
          border: 'none',
          borderBottom: !previewMode && band === 'header' ? `1px dashed ${edgeColor}` : undefined,
          borderTop: !previewMode && band === 'footer' ? `1px dashed ${edgeColor}` : undefined,
          background: 'transparent',
          zIndex: 2,
          pointerEvents: 'none',
        }}
      >
        {activeBand === band && guideV != null ? (
          <div
            style={{
              position: 'absolute',
              left: guideV,
              top: 0,
              bottom: 0,
              borderLeft: '1px dashed #3b82f6',
              pointerEvents: 'none',
              zIndex: 50,
            }}
          />
        ) : null}
        {activeBand === band && guideH != null ? (
          <div
            style={{
              position: 'absolute',
              top: guideH,
              left: 0,
              right: 0,
              borderTop: '1px dashed #3b82f6',
              pointerEvents: 'none',
              zIndex: 50,
            }}
          />
        ) : null}
      </div>
    );
  };

  const renderBandSideLabel = (band: LayoutBand) => {
    if (previewMode) return null;
    const height = band === 'header' ? layout.header.heightPt : layout.footer.heightPt;
    const topPt = band === 'header' ? 8 : footerTop + 8;
    return (
      <div
        key={`label-${band}`}
        style={{
          position: 'absolute',
          left: PDF_A4_WIDTH_PT * zoom + 10,
          top: topPt * zoom,
          zIndex: 5,
          pointerEvents: 'none',
          // Safari only honours the prefixed property; React does not add it.
          WebkitUserSelect: 'none',
          userSelect: 'none',
          lineHeight: 1.25,
        }}
      >
        <div
          style={{
            fontSize: 11,
            fontWeight: 600,
            color: '#94a3b8',
            letterSpacing: '0.02em',
            whiteSpace: 'nowrap',
          }}
        >
          {band === 'header' ? 'Kopfbereich' : 'Fußbereich'}
        </div>
        <div
          style={{
            fontSize: 11,
            fontWeight: 500,
            color: '#94a3b8',
            whiteSpace: 'nowrap',
          }}
        >
          {Math.round(height)} pt
        </div>
      </div>
    );
  };

  const renderBandResizeHandle = (band: LayoutBand) => {
    if (previewMode) return null;
    const y =
      band === 'header'
        ? layout.header.heightPt - 5
        : footerTop - 5;
    return (
      <div
        key={`resize-${band}`}
        data-band-resize
        title={band === 'header' ? 'Kopfbereich-Höhe anpassen' : 'Fußbereich-Höhe anpassen'}
        onMouseDown={(e) => startBandResize(band, e)}
        style={{
          position: 'absolute',
          left: 0,
          right: 0,
          top: y,
          height: 10,
          cursor: 'ns-resize',
          zIndex: 2500,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <div
          style={{
            width: 48,
            height: 4,
            borderRadius: 2,
            background: '#cbd5e1',
            boxShadow: '0 0 0 1px #fff',
          }}
        />
      </div>
    );
  };

  const LABEL_GUTTER_PX = previewMode ? 0 : 96;

  return (
    <div
      ref={viewportRef}
      className="relative flex flex-1 overflow-auto bg-app-surface-muted p-8 app-scrollbar"
      // "safe" falls back to left-aligned once the zoomed page is wider than the
      // viewport — plain centering makes the left overflow unreachable by scroll.
      style={{ justifyContent: 'safe center' }}
      onScroll={recordViewportCentre}
      onDragOver={onDragOver}
      onDrop={onDrop}
      onClick={() => {
        if (skipClickDeselectRef.current) {
          skipClickDeselectRef.current = false;
          return;
        }
        if (editingFreeTextId) finishFreeTextEdit();
        if (!previewMode) onSelect(null);
      }}
    >
      <div
        style={{
          width: PDF_A4_WIDTH_PT * zoom + LABEL_GUTTER_PX,
          height: (showPage2 ? PDF_A4_HEIGHT_PT * 2 + PREVIEW_PAGE_GAP_PT : PDF_A4_HEIGHT_PT) * zoom,
          position: 'relative',
          flexShrink: 0,
        }}
      >
        <div
          ref={canvasRef}
          onMouseDown={startMarquee}
          style={{
            width: PDF_A4_WIDTH_PT,
            height: PDF_A4_HEIGHT_PT,
            transform: `scale(${zoom})`,
            transformOrigin: 'top left',
            background: '#fff',
            boxShadow: '0 1px 3px rgba(0,0,0,.07), 0 12px 32px rgba(0,0,0,.08)',
            position: 'absolute',
            left: 0,
            top: 0,
          }}
        >
          {/* Flow body preview between header and footer (not editable) */}
          <div
            ref={page1BodyRef}
            style={{
              position: 'absolute',
              left: 0,
              top: bodyTop,
              right: 0,
              bottom: bodyBottom,
              background: '#fff',
              pointerEvents: 'none',
              zIndex: 1,
              overflow: 'hidden',
              paddingLeft: pageMargins.left,
              paddingRight: pageMargins.right,
              boxSizing: 'border-box',
            }}
          >
            <BodyFlowPreview
              tableHeaderBg={layout.body.tableHeaderBg || '#eceef1'}
              fontSizePt={layout.body.fontSizePt || 9.5}
              tableStartsOnPage2={tableStartsOnPage2}
              introParas={introParas}
              part={tableStartsOnPage2 ? 'intro' : 'all'}
            />
            {tablePlacement === 'auto' ? (
              <div
                ref={autoMeasureRef}
                aria-hidden="true"
                style={{
                  position: 'absolute',
                  top: 0,
                  left: pageMargins.left,
                  right: pageMargins.right,
                  visibility: 'hidden',
                }}
              >
                <BodyFlowPreview
                  tableHeaderBg={layout.body.tableHeaderBg || '#eceef1'}
                  fontSizePt={layout.body.fontSizePt || 9.5}
                  introParas={introParas}
                />
              </div>
            ) : null}
          </div>

          {!previewMode && showGuides ? (
            <div
              aria-hidden="true"
              style={{
                position: 'absolute',
                left: pageMargins.left,
                top: pageMargins.top,
                right: pageMargins.right,
                bottom: pageMargins.bottom,
                border: '1px dashed rgba(37, 99, 235, 0.45)',
                pointerEvents: 'none',
                zIndex: 3,
              }}
            />
          ) : null}

          {layoutShowsFoldMarks(layout) ? (
            <div aria-hidden="true" style={{ position: 'absolute', left: 0, top: 0, width: 0, height: 0, zIndex: 4 }}>
              {resolveDocumentFoldMarkRects().map((mark, index) => (
                <div
                  key={`fold-mark-${mark.kind}-${index}`}
                  style={{
                    position: 'absolute',
                    left: mark.xPt,
                    top: mark.yPt,
                    width: mark.wPt,
                    height: mark.hPt,
                    backgroundColor: DOCUMENT_FOLD_MARK_COLOR,
                    pointerEvents: 'none',
                  }}
                />
              ))}
            </div>
          ) : null}

          {renderBandFrame('header')}
          {renderBandFrame('footer')}

          {[...allElements]
            .sort((a, b) => (a.z ?? 0) - (b.z ?? 0))
            .map((el) => {
              const selected = !previewMode && selectedId === el.id;
              const inGroup = !previewMode && multiSelectIds.includes(el.id);
              const bandTop = el.band === 'footer' ? footerTop : 0;
              const isEditingFreeText = editingFreeTextId === el.id;
              const isDesign = [
                'logo',
                'headerBanner',
                'signatureImage',
                'divider',
                'colorBlock',
                'freeText',
              ].includes(el.key);
              const verticalLine = el.key === 'divider' && resolveLineOrientation(el) === 'vertical';
              const horizontalLine = el.key === 'divider' && !verticalLine;
              const lineHitPt = 8;
              // Same clamp as PDF and HTML bands so a hairline keeps its exported width.
              const lineThickness = el.key === 'divider' ? lineThicknessPt(el) : 0;
              const hitPadX = verticalLine ? Math.max(0, (lineHitPt - lineThickness) / 2) : 0;
              const hitPadY = horizontalLine ? Math.max(0, (lineHitPt - lineThickness) / 2) : 0;
              return (
                <div
                  key={el.id}
                  data-id={el.id}
                  onClick={(e) => {
                    e.stopPropagation();
                    if (previewMode) return;
                    if (isEditingFreeText) return;
                    onBandChange(el.band);
                    onSelect(el.id, { shift: e.shiftKey });
                  }}
                  onDoubleClick={(e) => {
                    e.stopPropagation();
                    if (previewMode) return;
                    if (el.key === 'freeText') beginFreeTextEdit(el);
                  }}
                  onMouseDown={(e) => {
                    if (previewMode) return;
                    if ((e.target as HTMLElement).closest('[data-resize]')) return;
                    if ((e.target as HTMLElement).closest('[data-freetext-edit]')) return;
                    if (isEditingFreeText) return;
                    // Second click of a double-click: don't start a drag.
                    if (e.detail > 1 && el.key === 'freeText') return;
                    // Shift+click is multi-select only (handled in onClick).
                    if (e.shiftKey) {
                      e.stopPropagation();
                      return;
                    }
                    if (editingFreeTextId && editingFreeTextId !== el.id) {
                      finishFreeTextEdit();
                    }
                    onBandChange(el.band);
                    const inMulti = multiSelectIds.includes(el.id);
                    if (!inMulti) {
                      onSelect(el.id);
                    }
                    const groupIds =
                      inMulti && multiSelectIds.length > 1
                        ? multiSelectIds.filter((id) => {
                            const other = allElements.find((x) => x.id === id);
                            return other?.band === el.band;
                          })
                        : [el.id];
                    startDrag(el, e, groupIds);
                  }}
                  onContextMenu={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    if (previewMode) return;
                    if (editingFreeTextId) finishFreeTextEdit();
                    onBandChange(el.band);
                    onSelect(el.id);
                    onContextMenu(el.id, e.clientX, e.clientY);
                  }}
                  style={{
                    position: 'absolute',
                    left: el.xPt - hitPadX,
                    top: bandTop + el.yPt - hitPadY,
                    width: el.wPt + hitPadX * 2,
                    height: el.hPt + hitPadY * 2,
                    zIndex: 10 + (el.z ?? 0) + (selected || inGroup || isEditingFreeText ? 1000 : 0),
                    opacity: 1,
                    border: previewMode
                      ? 'none'
                      : selected || isEditingFreeText
                        ? '1px solid #3b82f6'
                        : inGroup
                          ? '1px solid #93c5fd'
                          : el.key === 'divider'
                            ? 'none'
                            : isDesign
                              ? '1px dashed #cbd5e1'
                              : '1px solid #e2e8f0',
                    // Selection ring outside the box so padding/content don't jump when selected.
                    boxShadow:
                      !previewMode && (selected || isEditingFreeText)
                        ? '0 0 0 1px #3b82f6'
                        : !previewMode && inGroup
                          ? '0 0 0 1px #93c5fd'
                          : undefined,
                    background:
                      el.key === 'colorBlock'
                        ? el.bg || '#eff6ff'
                        : el.key === 'divider'
                          ? 'transparent'
                          : el.key === 'logo'
                            ? 'transparent'
                            : el.bg || 'transparent',
                    borderRadius: el.radiusPt ?? 0,
                    cursor: previewMode
                      ? 'default'
                      : el.locked
                        ? 'not-allowed'
                        : isEditingFreeText
                          ? 'text'
                          : 'grab',
                    padding: 0,
                    // Visible only while selected so resize handles aren't clipped;
                    // content itself is always clipped by the inner wrapper.
                    overflow:
                      el.key === 'divider' || ((selected || isEditingFreeText) && !previewMode)
                        ? 'visible'
                        : 'hidden',
                    boxSizing: 'border-box',
                    WebkitUserSelect: isEditingFreeText ? 'text' : 'none',
                    userSelect: isEditingFreeText ? 'text' : 'none',
                  }}
                >
                  {!previewMode && (selected || el.locked || isEditingFreeText) ? (
                    <div
                      style={{
                        position: 'absolute',
                        top: -18,
                        left: 0,
                        fontSize: 10,
                        fontWeight: 600,
                        color: '#fff',
                        background: '#3b82f6',
                        padding: '2px 6px',
                        whiteSpace: 'nowrap',
                        zIndex: 2,
                        pointerEvents: 'none',
                      }}
                    >
                      {layoutElementLabel(el.key, el.band)}
                      {el.locked ? ' · gesperrt' : ''}
                    </div>
                  ) : null}
                  {el.key === 'divider' ? (
                    <div
                      style={{
                        position: 'absolute',
                        left: hitPadX,
                        top: hitPadY,
                        width: verticalLine ? lineThickness : el.wPt,
                        height: horizontalLine ? lineThickness : el.hPt,
                        background: el.color || '#111827',
                        pointerEvents: 'none',
                      }}
                    />
                  ) : null}
                  <div
                    style={{
                      position: 'absolute',
                      inset: 0,
                      overflow: isEditingFreeText ? 'visible' : 'hidden',
                      // No editor padding at all — element boxes are absolute PDF
                      // coordinates, so any inset here would shift the export.
                      padding: 0,
                      boxSizing: 'border-box',
                      borderRadius: el.radiusPt ?? 0,
                    }}
                  >
                    {isEditingFreeText ? (
                      <textarea
                        ref={freeTextEditRef}
                        data-freetext-edit
                        value={freeTextDraft}
                        onChange={(e) => setFreeTextDraft(e.target.value)}
                        onMouseDown={(e) => e.stopPropagation()}
                        onClick={(e) => e.stopPropagation()}
                        onBlur={() => finishFreeTextEdit()}
                        onKeyDown={(e) => {
                          if (e.key === 'Escape') {
                            e.preventDefault();
                            finishFreeTextEdit({ cancel: true });
                          } else if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
                            e.preventDefault();
                            finishFreeTextEdit();
                          }
                        }}
                        spellCheck={false}
                        style={{
                          width: '100%',
                          height: '100%',
                          resize: 'none',
                          border: 'none',
                          outline: 'none',
                          padding: 0,
                          margin: 0,
                          background: 'transparent',
                          fontSize: el.fontSizePt ?? 9.5,
                          fontWeight: el.bold ? 700 : 400,
                          fontStyle: el.italic ? 'italic' : 'normal',
                          textDecoration: el.underline ? 'underline' : 'none',
                          textDecorationSkipInk: 'none',
                          textAlign: el.align ?? 'left',
                          color: el.color || '#374151',
                          lineHeight: 1.35,
                          ...OFFER_PDF_FONT_STYLE,
                          boxSizing: 'border-box',
                          cursor: 'text',
                        }}
                        aria-label="Freitext bearbeiten"
                      />
                    ) : (
                      renderElementContent(
                        el,
                        previewMode,
                        logoUrl,
                        sampleData,
                        assetUrls[el.id] ?? null,
                        hideOfferTitleTypePrefix,
                      )
                    )}
                  </div>
                  {selected && !el.locked && !previewMode && !isEditingFreeText
                    ? resizeHandles.map((h) => (
                        <div
                          key={h.edge}
                          data-resize
                          onMouseDown={(e) => startResize(el, e, h.edge)}
                          style={{
                            position: 'absolute',
                            zIndex: 3,
                            cursor: h.cursor,
                            ...h.style,
                          }}
                        >
                          {h.knob ? (
                            <div style={h.knob} />
                          ) : (
                            <div
                              style={{
                                width: '100%',
                                height: '100%',
                                borderRadius: 2,
                                background: '#3b82f6',
                                border: '1px solid #fff',
                                boxSizing: 'border-box',
                              }}
                            />
                          )}
                        </div>
                      ))
                    : null}
                </div>
              );
            })}
          {renderBandResizeHandle('header')}
          {renderBandResizeHandle('footer')}
          {marquee ? (
            <div
              style={{
                position: 'absolute',
                left: Math.min(marquee.x0, marquee.x1),
                top: Math.min(marquee.y0, marquee.y1),
                width: Math.abs(marquee.x1 - marquee.x0),
                height: Math.abs(marquee.y1 - marquee.y0),
                border: '1px solid #3b82f6',
                background: 'rgba(59, 130, 246, 0.12)',
                pointerEvents: 'none',
                zIndex: 5000,
                boxSizing: 'border-box',
              }}
            />
          ) : null}
        </div>
        {renderBandSideLabel('header')}
        {renderBandSideLabel('footer')}
        {showPage2 ? (
          <div
            aria-hidden="true"
            style={{
              width: PDF_A4_WIDTH_PT,
              height: PDF_A4_HEIGHT_PT,
              transform: `scale(${zoom})`,
              transformOrigin: 'top left',
              background: '#fff',
              boxShadow: '0 1px 3px rgba(0,0,0,.07), 0 12px 32px rgba(0,0,0,.08)',
              position: 'absolute',
              left: 0,
              top: (PDF_A4_HEIGHT_PT + PREVIEW_PAGE_GAP_PT) * zoom,
              pointerEvents: 'none',
              overflow: 'hidden',
            }}
          >
            <div
              style={{
                position: 'absolute',
                left: 0,
                top: pageMargins.top,
                right: 0,
                bottom: bodyBottom,
                overflow: 'hidden',
                paddingLeft: pageMargins.left,
                paddingRight: pageMargins.right,
                boxSizing: 'border-box',
              }}
            >
              <div style={tableStartsOnPage2 ? undefined : { marginTop: -page1BodyHeight }}>
                <BodyFlowPreview
                  tableHeaderBg={layout.body.tableHeaderBg || '#eceef1'}
                  fontSizePt={layout.body.fontSizePt || 9.5}
                  tableStartsOnPage2={tableStartsOnPage2}
                  introParas={introParas}
                  part={tableStartsOnPage2 ? 'rest' : 'all'}
                />
              </div>
            </div>
            {layoutShowsFoldMarks(layout)
              ? resolveDocumentFoldMarkRects().map((mark, index) => (
                  <div
                    key={`p2-fold-${mark.kind}-${index}`}
                    style={{
                      position: 'absolute',
                      left: mark.xPt,
                      top: mark.yPt,
                      width: mark.wPt,
                      height: mark.hPt,
                      backgroundColor: DOCUMENT_FOLD_MARK_COLOR,
                    }}
                  />
                ))
              : null}
            {[...layout.footer.elements]
              .sort((a, b) => (a.z ?? 0) - (b.z ?? 0))
              .map((el) => {
                const vertical = el.key === 'divider' && resolveLineOrientation(el) === 'vertical';
                const thickness = el.key === 'divider' ? lineThicknessPt(el) : 0;
                return (
                  <div
                    key={`p2-${el.id}`}
                    style={{
                      position: 'absolute',
                      left: el.xPt,
                      top: footerTop + el.yPt,
                      width: el.wPt,
                      height: el.hPt,
                      zIndex: 2 + (el.z ?? 0),
                      overflow: 'hidden',
                      borderRadius: el.radiusPt ?? 0,
                    }}
                  >
                    {el.key === 'divider' ? (
                      <div
                        style={{
                          width: vertical ? thickness : el.wPt,
                          height: vertical ? el.hPt : thickness,
                          background: el.color || '#111827',
                        }}
                      />
                    ) : (
                      renderElementContent(
                        el,
                        true,
                        logoUrl,
                        sampleData,
                        assetUrls[el.id] ?? null,
                        hideOfferTitleTypePrefix,
                      )
                    )}
                  </div>
                );
              })}
          </div>
        ) : null}
      </div>
    </div>
  );
}
